import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { A, BRAND, CONTACT_EMAIL, CSS, Lnk, StoreBadges } from './shared';

const NAV: [string, string][] = [
  ['Features', '/features'],
  ['Solutions', '/solutions'],
  ['Pricing', '/pricing'],
  ['Security', '/security'],
  ['About', '/about'],
];

const FOOTER_COLS: { h: string; items: [string, string][] }[] = [
  { h: 'Product', items: [['Features', '/features'], ['Security', '/security'], ['Pricing', '/pricing'], ['Create a community', '/get-started'], ['Sign in', '/sign-in']] },
  { h: 'Solutions', items: [['Fraternities & sororities', '/solutions/fraternities-sororities'], ['Civic organizations', '/solutions/civic-organizations'], ['Alumni chapters', '/solutions/alumni-chapters'], ['National organizations', '/solutions/national-organizations']] },
  { h: 'Company', items: [['About', '/about'], ['Request a demo', '/contact'], [CONTACT_EMAIL, `mailto:${CONTACT_EMAIL}`]] },
];

/** Reset scroll position on route change. */
const ScrollTop: React.FC = () => {
  const { pathname } = useLocation();
  const previousPath = useRef(pathname);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (previousPath.current !== pathname) {
      document.getElementById('main')?.focus({ preventScroll: true });
      previousPath.current = pathname;
    }
  }, [pathname]);
  return null;
};

const Brand: React.FC<{ light?: boolean }> = ({ light }) => (
  <Lnk to="/" className="brand" aria-label={`${BRAND} home`}>
    <img src={`${A}/logo-mark.png`} alt="" className="brand-mark" />
    <span className={`brand-word${light ? ' light' : ''}`}>KONKLAVE</span>
  </Lnk>
);

const MarketingLayout: React.FC = () => {
  const toggleRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    // Use the Konklave favicon set on the marketing site.
    document
      .querySelectorAll("link[rel~='icon'], link[rel='apple-touch-icon']")
      .forEach((n) => n.parentNode?.removeChild(n));
    const add = (rel: string, href: string, attrs: Record<string, string> = {}) => {
      const l = document.createElement('link');
      l.rel = rel; l.href = href;
      Object.entries(attrs).forEach(([k, v]) => l.setAttribute(k, v));
      document.head.appendChild(l);
    };
    add('icon', `${A}/favicon.ico`);
    add('icon', `${A}/favicon-32.png`, { type: 'image/png', sizes: '32x32' });
    add('apple-touch-icon', `${A}/apple-touch-icon.png`);
  }, []);

  return (
    <div className="kk">
      <style>{CSS}</style>
      <ScrollTop />
      <a href="#main" className="skip">Skip to content</a>

      <header className="nav">
        <div className="container nav-inner">
          <Brand />
          <nav className="nav-links" aria-label="Main">
            {NAV.map(([label, to]) => <NavLink key={to} to={to}>{label}</NavLink>)}
          </nav>
          <div className="nav-actions">
            <Lnk to="/sign-in" className="btn btn-outline">Sign in</Lnk>
            <Lnk to="/get-started" className="btn btn-primary">Create your community</Lnk>
            <button
              ref={toggleRef}
              type="button"
              className="nav-toggle"
              aria-expanded={open}
              aria-controls="nav-drawer"
              aria-label={open ? 'Close menu' : 'Open menu'}
              onClick={() => setOpen((o) => !o)}
            >
              {open ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>
        <div id="nav-drawer" onKeyDown={(event) => {
          if (event.key === 'Escape') { setOpen(false); toggleRef.current?.focus(); }
        }} className={`nav-drawer${open ? ' open' : ''}`}>
          <nav aria-label="Mobile">
            {NAV.map(([label, to]) => <Lnk key={to} to={to}>{label}</Lnk>)}
            <Lnk to="/sign-in">Sign in</Lnk>
            <Lnk to="/get-started" className="btn btn-primary">Create your community</Lnk>
          </nav>
        </div>
      </header>

      <main id="main" tabIndex={-1}><Outlet /></main>

      <footer className="footer">
        <div className="container footer-grid">
          <div className="footer-brand">
            <Brand light />
            <p className="footer-tag">Connect, engage, empower.</p>
            <p>One app where fraternities, sororities, civic groups, and alumni networks run their communities.</p>
            <StoreBadges dark />
          </div>
          {FOOTER_COLS.map((col) => (
            <div className="footer-col" key={col.h}>
              <h2>{col.h}</h2>
              {col.items.map(([label, to]) => <Lnk key={label} to={to}>{label}</Lnk>)}
            </div>
          ))}
        </div>
        <div className="container footer-bottom">
          <span>© {new Date().getFullYear()} {BRAND}. All rights reserved.</span>
          <nav aria-label="Legal">
            <Lnk to="/privacy">Privacy</Lnk>
            <Lnk to="/terms">Terms</Lnk>
            <Lnk to="/security">Security</Lnk>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default MarketingLayout;
